import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { JobHttpService } from 'cms/features/feature_job/service';
import SalarySpecificationForm from '../components/SalarySpecificationForm';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

function SalarySpecificationsPage() {
    const [formOpen, setFormOpen] = useState(false);
    const [selectedSpecificationId, setSelectedSpecificationId] = useState(null);
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Load user's company
    const { data: company, isLoading: companyLoading } = useQuery(
        'myCompany',
        () => JobHttpService.loadMyCompany()
    );

    // Load salary specifications for the company
    const { data: specificationsData, isLoading: specificationsLoading, isError, error } = useQuery(
        ['salarySpecifications', company?.id],
        () => {
            if (!company?.id) return { results: [], count: 0 };
            return JobHttpService.loadSalarySpecifications({ company: company.id });
        },
        {
            enabled: !!company?.id,
            keepPreviousData: true
        }
    );

    const specifications = specificationsData?.results || specificationsData || [];

    const handleAddClick = () => {
        setSelectedSpecificationId(null);
        setFormOpen(true);
    };

    const handleItemClick = (specificationId) => {
        navigate(`/employer/salary-specification/${specificationId}`);
    };

    const handleFormClose = () => {
        setFormOpen(false);
        setSelectedSpecificationId(null);
    };

    const handleSpecificationSaved = () => {
        queryClient.invalidateQueries(['salarySpecifications', company?.id]);
    };

    const handleSaveAndAddAnother = () => {
        queryClient.invalidateQueries(['salarySpecifications', company?.id]);
        // Form will stay open with reset data
    };

    if (companyLoading || specificationsLoading) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </section>
        );
    }

    if (!company) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        You need to create a company first. <Button href="/employer/my-company">Go to My Company</Button>
                    </Alert>
                </Container>
            </section>
        );
    }

    return (
        <section className="section-box mt-50">
            <Container maxWidth="lg">
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" fontWeight={600} gutterBottom>
                        Salary Specifications
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage salary specifications for your employees
                    </Typography>
                </Box>

                {isError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error?.message || 'Failed to load salary specifications'}
                    </Alert>
                )}

                {specifications.length === 0 && !specificationsLoading && (
                    <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
                        <AccountBalanceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            No salary specifications found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Start by adding your first salary specification
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddClick}
                        >
                            Add Salary Specification
                        </Button>
                    </Paper>
                )}

                {specifications.length > 0 && (
                    <Card>
                        <CardContent sx={{ p: 0 }}>
                            <List>
                                {specifications.map((spec, index) => {
                                    const employee = spec.employee;
                                    const employeeName = employee 
                                        ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim()
                                        : 'Unknown Employee';
                                    const periodStart = spec.salary_period_start 
                                        ? new Date(spec.salary_period_start).toLocaleDateString()
                                        : '';
                                    const periodEnd = spec.salary_period_end 
                                        ? new Date(spec.salary_period_end).toLocaleDateString()
                                        : '';
                                    const dateRange = periodStart && periodEnd 
                                        ? `${periodStart} - ${periodEnd}`
                                        : periodStart || periodEnd || 'No date range';

                                    return (
                                        <React.Fragment key={spec.id}>
                                            <ListItemButton onClick={() => handleItemClick(spec.id)}>
                                                <ListItemText
                                                    primary={employeeName}
                                                    secondary={dateRange}
                                                    primaryTypographyProps={{
                                                        fontWeight: 500,
                                                        fontSize: '1rem'
                                                    }}
                                                    secondaryTypographyProps={{
                                                        fontSize: '0.875rem',
                                                        color: 'text.secondary'
                                                    }}
                                                />
                                                <ArrowForwardIcon color="action" />
                                            </ListItemButton>
                                            {index < specifications.length - 1 && <Divider />}
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        </CardContent>
                    </Card>
                )}

                {specifications.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Button
                            variant="text"
                            startIcon={<AddIcon />}
                            onClick={handleAddClick}
                            sx={{ color: 'primary.main' }}
                        >
                            Add another salary specification
                        </Button>
                    </Box>
                )}

                {/* Salary Specification Form Modal */}
                <SalarySpecificationForm
                    open={formOpen}
                    onClose={handleFormClose}
                    salarySpecificationId={selectedSpecificationId}
                    companyId={company.id}
                    onSaved={handleSpecificationSaved}
                    onSaveAndAddAnother={handleSaveAndAddAnother}
                />
            </Container>
        </section>
    );
}

export default SalarySpecificationsPage;
